import React from 'react';

type BadgeVariant = 'quantum' | 'health' | 'ecosystem' | 'economic' | 'neutral' | 'success' | 'warning' | 'error';
type BadgeSize = 'sm' | 'md';

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  icon?: React.ReactNode;
  dot?: boolean;
  className?: string;
}

const variantClasses: Record<BadgeVariant, string> = {
  quantum: 'bg-quantum-500/15 text-quantum-400 border-quantum-500/30',
  health: 'bg-health-500/15 text-health-400 border-health-500/30',
  ecosystem: 'bg-ecosystem-500/15 text-ecosystem-400 border-ecosystem-500/30',
  economic: 'bg-economic-500/15 text-economic-400 border-economic-500/30',
  neutral: 'bg-surface-600 text-text-secondary border-border',
  success: 'bg-quantum-500/15 text-quantum-400 border-quantum-500/30',
  warning: 'bg-economic-500/15 text-economic-400 border-economic-500/30',
  error: 'bg-health-500/15 text-health-400 border-health-500/30',
};

const dotColors: Record<BadgeVariant, string> = {
  quantum: 'bg-quantum-500',
  health: 'bg-health-500',
  ecosystem: 'bg-ecosystem-500',
  economic: 'bg-economic-500',
  neutral: 'bg-text-muted',
  success: 'bg-quantum-500',
  warning: 'bg-economic-500',
  error: 'bg-health-500',
};

const sizeClasses: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-[0.6875rem]',
  md: 'px-2.5 py-0.5 text-caption',
};

export function Badge({
  children,
  variant = 'neutral',
  size = 'md',
  icon,
  dot = false,
  className = '',
}: BadgeProps) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border font-medium ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
    >
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${dotColors[variant]}`} />}
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {children}
    </span>
  );
}

// Status badge for run states
type RunStatus = 'queued' | 'running' | 'completed' | 'failed' | 'pending';

const statusConfig: Record<RunStatus, { variant: BadgeVariant; label: string }> = {
  queued: { variant: 'neutral', label: 'Queued' },
  running: { variant: 'ecosystem', label: 'Running' },
  completed: { variant: 'success', label: 'Completed' },
  failed: { variant: 'error', label: 'Failed' },
  pending: { variant: 'warning', label: 'Pending' },
};

interface StatusBadgeProps {
  status: RunStatus;
  className?: string;
}

export function StatusBadge({ status, className }: StatusBadgeProps) {
  const config = statusConfig[status];
  return (
    <Badge variant={config.variant} dot className={className}>
      {config.label}
    </Badge>
  );
}

// Tech type badge
type TechType = 'solar' | 'wind';

const techConfig: Record<TechType, { icon: string; label: string }> = {
  solar: { icon: '☀️', label: 'Solar' },
  wind: { icon: '💨', label: 'Wind' },
};

interface TechBadgeProps {
  tech: TechType;
  className?: string;
}

export function TechBadge({ tech, className }: TechBadgeProps) {
  const config = techConfig[tech];
  if (!config) return null;
  return (
    <Badge variant="neutral" className={className}>
      <span>{config.icon}</span>
      {config.label}
    </Badge>
  );
}

// Generation-profile provenance badge. Generation is a primary driver of
// results — this surfaces how the profile was derived so reviewers can
// weight confidence (metered > calibrated > estimated).
type ProfileSource =
  | 'metered'
  | 'calibrated'
  | 'estimated'
  | 'gap_filled'
  | 'mixed';

const profileConfig: Record<
  ProfileSource,
  { variant: BadgeVariant; label: string; hint: string }
> = {
  metered:    { variant: 'quantum',  label: 'Metered',    hint: 'Directly measured generation' },
  calibrated: { variant: 'ecosystem', label: 'Calibrated', hint: 'SAM shape rescaled to metered aggregate totals' },
  estimated:  { variant: 'economic', label: 'Estimated',  hint: 'SAM-modeled, no metered reconciliation' },
  gap_filled: { variant: 'warning',  label: 'Gap-filled', hint: 'Missing hours filled (mostly zeros)' },
  mixed:      { variant: 'neutral',  label: 'Mixed',      hint: 'Multiple provenance sources' },
};

interface ProfileSourceBadgeProps {
  source?: ProfileSource | string | null;
  meteredPct?: number | null;
  breakdown?: Record<string, number> | null;
  size?: BadgeSize;
  className?: string;
}

export function ProfileSourceBadge({
  source,
  meteredPct,
  breakdown,
  size = 'sm',
  className,
}: ProfileSourceBadgeProps) {
  if (!source) return null;
  const cfg = profileConfig[source as ProfileSource] ?? profileConfig.mixed;
  // Build a title tooltip from the raw breakdown for auditability.
  const tip = breakdown
    ? Object.entries(breakdown)
        .sort((a, b) => b[1] - a[1])
        .map(([k, v]) => `${k}: ${v}`)
        .join('\n')
    : cfg.hint;
  return (
    <span title={tip} className="inline-flex">
      <Badge variant={cfg.variant} size={size} dot className={className}>
        {cfg.label}
        {typeof meteredPct === 'number' && source === 'mixed' && (
          <span className="opacity-70">· {Math.round(meteredPct)}% met</span>
        )}
      </Badge>
    </span>
  );
}
